class GiftWrapping {
  constructor() {
    this.isUpdating = false;
    this.retryCount = 0;
    this.maxRetries = 3;
    this.lastCartState = null;
    this.cartStateTimestamp = 0;
    this.init();
  }

  init() {
    this.bindEvents();
    this.updateButtonState();
  }

  // Debounce function to prevent excessive API calls
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  bindEvents() {
    // Create debounced update function
    this.debouncedUpdate = this.debounce(this.updateButtonState.bind(this), 500);

    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-gift-wrap-add]')) {
        e.preventDefault();
        const button = e.target.closest('[data-gift-wrap-add]');
        const variantId = button.dataset.variantId;
        
        if (variantId) {
          this.addGiftWrapping(variantId, button);
        }
      }

      if (e.target.closest('[data-item-remove]')) {
        setTimeout(() => {
          this.debouncedUpdate();
        }, 200);
      }
    });

    // Use debounced updates for all cart events
    document.addEventListener('theme:cart:updated', this.debouncedUpdate);
    document.addEventListener('theme:cart:change', this.debouncedUpdate);
    document.addEventListener('theme:cart-drawer:open', this.debouncedUpdate);
    document.addEventListener('free-gift:added', this.debouncedUpdate);
    document.addEventListener('free-gift:removed', this.debouncedUpdate);

    this.observeCartChanges();
  }

  observeCartChanges() {
    const cartItemsContainer = document.querySelector('[data-items-holder]');
    if (cartItemsContainer) {
      const observer = new MutationObserver(() => {
        this.debouncedUpdate();
      });
      
      observer.observe(cartItemsContainer, {
        childList: true,
        subtree: true
      });
    }
  }

  async updateButtonState() {
    if (this.isUpdating) return;
    
    const addButton = document.querySelector('[data-gift-wrap-add]');
    const giftWrapContainer = document.querySelector('.cart__gift-wrapping');
    
    if (!addButton || !giftWrapContainer) return;

    this.isUpdating = true;
    this.retryCount = 0;

    try {
      await this.performUpdate(addButton, giftWrapContainer);
    } catch (error) {
      console.error('Error updating gift wrapping state:', error);
      this.handleUpdateError(addButton, giftWrapContainer);
    } finally {
      this.isUpdating = false;
    }
  }

  async performUpdate(addButton, giftWrapContainer) {
    // Check if we have recent cart state cached
    if (this.lastCartState && Date.now() - this.cartStateTimestamp < 2000) {
      const giftWrapInCart = this.lastCartState.some(item => 
        item.variant_id.toString() === addButton.dataset.variantId
      );
      
      this.updateGiftWrapDisplay(giftWrapContainer, giftWrapInCart);
      return;
    }

    const cartResponse = await fetch('/cart.js', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache'
      }
    });

    if (!cartResponse.ok) {
      throw new Error(`Cart API error: ${cartResponse.status}`);
    }

    const cart = await cartResponse.json();
    
    // Cache the cart state
    this.lastCartState = cart.items;
    this.cartStateTimestamp = Date.now();
    
    const giftWrapInCart = cart.items.some(item => 
      item.variant_id.toString() === addButton.dataset.variantId
    );

    this.updateGiftWrapDisplay(giftWrapContainer, giftWrapInCart);
    this.retryCount = 0;
  }

  updateGiftWrapDisplay(giftWrapContainer, giftWrapInCart) {
    if (giftWrapInCart) {
      giftWrapContainer.style.display = 'none';
      giftWrapContainer.setAttribute('data-gift-wrap-status', 'added');
    } else {
      giftWrapContainer.style.display = 'block';
      giftWrapContainer.setAttribute('data-gift-wrap-status', 'available');
    }
  }

  async handleUpdateError(addButton, giftWrapContainer) {
    if (this.retryCount < this.maxRetries) {
      this.retryCount++;
      
      const delay = Math.pow(2, this.retryCount) * 100;
      setTimeout(() => {
        this.updateButtonState();
      }, delay);
    } else {
      console.error('Max retries reached for gift wrapping update');
      giftWrapContainer.style.display = 'block';
      giftWrapContainer.setAttribute('data-gift-wrap-status', 'error');
    }
  }

  async addGiftWrapping(variantId, button) {
    try {
      button.disabled = true;
      button.classList.add('loading');

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to add gift wrapping: ${response.status}`);
      }

      const result = await response.json();

      const giftWrapContainer = document.querySelector('.cart__gift-wrapping');
      if (giftWrapContainer) {
        giftWrapContainer.style.display = 'none';
        giftWrapContainer.setAttribute('data-gift-wrap-status', 'added');
      }

      const cartItems = document.querySelector('cart-items');
      if (cartItems && typeof cartItems.getCart === 'function') {
        try {
          await cartItems.getCart();
        } catch (cartError) {
          console.warn('Cart refresh failed, but gift wrapping was added:', cartError);
        }
      }

      document.dispatchEvent(new CustomEvent('gift-wrapping:added', {
        detail: { 
          variantId: variantId,
          timestamp: Date.now() 
        }
      }));

      setTimeout(() => {
        this.updateButtonState();
      }, 500);

    } catch (error) {
      console.error('Error adding gift wrapping:', error);
      
      const giftWrapContainer = document.querySelector('.cart__gift-wrapping');
      if (giftWrapContainer) {
        giftWrapContainer.setAttribute('data-gift-wrap-status', 'error');
      }
      
      button.disabled = false;
    } finally {
      button.classList.remove('loading');
    }
  }

  forceRefresh() {
    this.isUpdating = false;
    this.retryCount = 0;
    this.updateButtonState();
  }

  isInitialized() {
    const addButton = document.querySelector('[data-gift-wrap-add]');
    const giftWrapContainer = document.querySelector('.cart__gift-wrapping');
    return !!(addButton && giftWrapContainer);
  }
}

window.giftWrappingInstance = null;

document.addEventListener('DOMContentLoaded', () => {
  window.giftWrappingInstance = new GiftWrapping();
});

document.addEventListener('theme:cart:updated', () => {
  if (!window.giftWrappingInstance || !window.giftWrappingInstance.isInitialized()) {
    window.giftWrappingInstance = new GiftWrapping();
  } else {
    window.giftWrappingInstance.updateButtonState();
  }
});

document.addEventListener('theme:cart-drawer:open', () => {
  if (!window.giftWrappingInstance || !window.giftWrappingInstance.isInitialized()) {
    window.giftWrappingInstance = new GiftWrapping();
  } else {
    window.giftWrappingInstance.updateButtonState();
  }
});
